import { EXPLORER_MEASUREMENT_FIELDS, MONITORING_EXPLORER_TYPES } from '@cosmos/shared';

export type ExplorerTranslate = (key: string) => string;

/** A group-by / filter option: a measurement-level field or a type dimension (channel…). */
export interface ExplorerFieldOption {
  key: string;
  path: string;
  label: string;
  filterType: 'categorical';
  isDimension: boolean;
}

export interface VariableGroup {
  section: string;
  label: string;
  items: { key: string; label: string; unit: string }[];
}

/** One dataset as returned by the explorer endpoint. */
export interface ExplorerDatasetResponse {
  datasetLabel: string;
  groupParts?: string[];
  groupFields?: string[];
  variable?: string;
  axis?: 'primary' | 'secondary';
  dataPoints: { x: string | Date; y: number; count?: number }[];
}

const MEASUREMENT_FIELD_LABEL_KEYS: Record<string, string> = {
  'name': 'monitoring.labels.name',
  'device.name': 'monitoring.labels.deviceName',
  'active': 'monitoring.labels.activeStatus',
};

/** Project types that the explorer can handle (have a definition). */
export function explorerTypesForProject(projectTypes: string[]): string[] {
  return projectTypes.filter((t) => !!MONITORING_EXPLORER_TYPES[t]);
}

export function variableGroups(type: string | null, t: ExplorerTranslate): VariableGroup[] {
  const def = type ? MONITORING_EXPLORER_TYPES[type] : undefined;
  if (!def) return [];
  const groups = new Map<string, VariableGroup>();
  for (const v of def.variables) {
    if (!groups.has(v.section)) {
      groups.set(v.section, { section: v.section, label: t(`monitoring.data-explorer.sections.${v.section}`), items: [] });
    }
    groups.get(v.section)!.items.push({ key: v.key, label: t(v.labelKey), unit: v.unit });
  }
  return [...groups.values()];
}

/** Group-by AND filter options for a type: measurement fields, then the type's dimensions. Nothing is preselected. */
export function fieldOptions(type: string | null, t: ExplorerTranslate): ExplorerFieldOption[] {
  const def = type ? MONITORING_EXPLORER_TYPES[type] : undefined;
  if (!def) return [];
  return [
    ...EXPLORER_MEASUREMENT_FIELDS.map((f) => ({
      key: f, path: f, label: t(MEASUREMENT_FIELD_LABEL_KEYS[f]), filterType: 'categorical' as const, isDimension: false,
    })),
    ...def.dimensions.map((d) => ({
      key: d.key, path: d.key, label: t(d.labelKey), filterType: 'categorical' as const, isDimension: true,
    })),
  ];
}

/** "channel2" → "Channel 2"; anything else unchanged. */
export function formatChannelPart(value: string, channelWord: string): string {
  const m = /^channel(\d+)$/.exec(value);
  return m ? `${channelWord} ${m[1]}` : value;
}

/**
 * Identifying series label: every group part, then the variable —
 * "Measurement X - Channel 1 - Corrosion potential"; ungrouped → just the variable label.
 * Uses the structured `groupParts` (never splits the joined label, names may contain " - ").
 */
export function composeSeriesLabel(
  ds: { groupFields?: string[]; groupParts?: string[] },
  variableLabel: string,
  channelWord: string,
): string {
  const parts = (ds.groupParts ?? []).map((p, i) => (ds.groupFields?.[i] === 'channel' ? formatChannelPart(p, channelWord) : p));
  return [...parts, variableLabel].join(' - ');
}

export interface ExploreFormInput {
  measurementType: string | null;
  metricField: string | null;
  secondaryMetricField: string | null;
  dateRange: (Date | null)[] | null;
  groupByField: ExplorerFieldOption | null;
  groupByField2: ExplorerFieldOption | null;
  timeAggregationKey: string;
  aggregationKey: string;
  filterLogic: 'and' | 'or';
  filters: Array<{
    field: ExplorerFieldOption | null;
    selectedValues: (string | number | boolean)[];
    operator: string | null;
    numericValue: number | null;
  }>;
}

const isCompleteFilter = (f: ExploreFormInput['filters'][number]): boolean =>
  !!f.field && (f.field.filterType === 'categorical' ? f.selectedValues?.length > 0 : false);

export type QueryBadgeKey = 'filters' | 'grouping' | 'aggregation' | 'dateRange';

/** A "this query has extra setup" indicator shown in the collapsed query-settings header. */
export interface QueryBadge {
  key: QueryBadgeKey;
  count?: number;
  detail?: string;
}

/** Which optional parts of the query are in use: filters (complete ones), grouping, aggregation, date range — in that order. */
export function buildQueryBadges(
  i: ExploreFormInput,
  labels: { timeAggregation: string; aggregation: string },
): QueryBadge[] {
  const badges: QueryBadge[] = [];
  const filters = i.filters.filter(isCompleteFilter).length;
  if (filters) badges.push({ key: 'filters', count: filters });
  const groups = [i.groupByField, i.groupByField2].filter(Boolean).length;
  if (groups) badges.push({ key: 'grouping', count: groups });
  if (i.timeAggregationKey !== 'none') badges.push({ key: 'aggregation', detail: `${labels.timeAggregation} · ${labels.aggregation}` });
  if (i.dateRange && i.dateRange[0] && i.dateRange[1]) badges.push({ key: 'dateRange' });
  return badges;
}

/** Form state → POST body for `/measurements/aggregation/data-explorer/:projectId`. */
export function buildExploreRequest(i: ExploreFormInput): Record<string, any> {
  const params: Record<string, any> = { metricField: i.metricField, timeAggregation: i.timeAggregationKey };
  if (i.measurementType) params['measurementType'] = i.measurementType;
  if (i.secondaryMetricField && i.timeAggregationKey !== 'group') params['secondaryMetricField'] = i.secondaryMetricField;
  if (i.dateRange && i.dateRange[0] && i.dateRange[1]) params['dateRange'] = { start: i.dateRange[0], end: i.dateRange[1] };
  if (i.groupByField) {
    params['groupByField'] = i.groupByField.path;
    if (i.groupByField2) params['groupByField2'] = i.groupByField2.path;
  }
  if (i.timeAggregationKey !== 'none') params['aggregation'] = i.aggregationKey || 'average';

  const valid = i.filters.filter(isCompleteFilter);
  if (valid.length) {
    params['filters'] = valid.map((f) => ({
      field: f.field!.path,
      filterType: f.field!.filterType,
      values: f.selectedValues,
      operator: undefined,
      numericValue: undefined,
    }));
    params['filterLogic'] = i.filterLogic;
  }
  return params;
}
